import isNumber from 'lodash/isNumber';
import isFunction from 'lodash/isFunction';
import isBoolean from 'lodash/isBoolean';
import {
  BarSeriesOption,
  DefaultLabelFormatterCallbackParams,
  LegendComponentOption,
} from 'echarts';
import { LabelConfig, RangeType, XFieldConfig, YFieldConfig } from './types';
import { BASE_DATA_ZOOM_CONFIG, INTERVAL_LABEL_MAP, SCROLL_BAR_HEIGHT } from './const';

export const getFormattedValue = (format?: unknown, ...rest: any[]): string | boolean => {
  try {
    if (isFunction(format)) {
      return format.apply(this, rest);
    }
    if (typeof format === 'string' || isNumber(format)) {
      return format.toString();
    }
    if (isBoolean(format)) {
      return format;
    }
    return '';
  } catch {
    return '';
  }
};

export const getFieldRange = (rangeSetting?: RangeType, range?: number[]) => {
  if (rangeSetting === 'custom' && range?.length) {
    return {
      min: range[0],
      max: range[1],
    };
  }

  if (rangeSetting === 'dataAuto') {
    return {
      min: 'dataMin',
      max: 'dataMax',
    };
  }

  return {
    min: null,
    max: null,
  };
};

const getFormatLabel =
  (formatLabel?: (originalLabel: string) => string) =>
  (params: DefaultLabelFormatterCallbackParams): string => {
    const { data, dimensionNames, encode } = params || {};
    if (dimensionNames && encode?.y[0]) {
      const original = (data as Record<string, string>)?.[dimensionNames?.[encode.y[0]]];
      return getFormattedValue(formatLabel || original, original) as string;
    }
    return '';
  };

// 计算堆叠总计值，用于展示label
const calcStackSum = (data: DefaultLabelFormatterCallbackParams['data'], yFieldKeys: string[]) => {
  if (typeof data === 'object') {
    const sum = yFieldKeys.reduce((accumulator, key) => {
      const value = (data as Record<string, string>)?.[key];
      return accumulator + (Number(value) || 0);
    }, 0);
    return String(sum);
  }

  return '';
};

/**
 * @description 展示数据标签：普通&分组折线图/柱状图
 */
export const getIntervalLabel = (labelConfig?: LabelConfig) => {
  const { showLabel, labelType, formatLabel } = labelConfig || {};

  if (!showLabel) {
    return { symbol: 'none' };
  }

  // 通过markPoint实现特殊标注(最大、最小值)
  if (labelType === 'max' || labelType === 'min' || labelType === 'maxAndMin') {
    return {
      symbol: 'circle',
      markPoint: {
        symbolSize: 12,
        itemStyle: {
          color: 'transparent',
        },
        label: {
          color: '#333',
          offset: [0, -6],
          formatter: (params: DefaultLabelFormatterCallbackParams) => {
            const originalValue = (params?.data as Record<string, string>).value;
            return getFormattedValue(formatLabel || originalValue, originalValue) as string;
          },
        },
        data: INTERVAL_LABEL_MAP[labelType],
      },
    };
  }

  // 通过label展示所有值
  return {
    symbol: 'circle',
    label: {
      show: true,
      position: 'top',
      formatter: getFormatLabel(formatLabel),
    },
  };
};

/**
 *  @description 展示数据标签：堆叠柱状图
 */
export const getStackIntervalLabel = (
  labelConfig?: LabelConfig,
  isLastSeries?: boolean,
  yFieldKeys: string[] = [],
): BarSeriesOption => {
  const { showLabel, stackLabelType, formatLabel } = labelConfig || {};

  if (!showLabel) {
    return { label: { show: false } };
  }

  if (isLastSeries) {
    // 仅展示数值时，隐藏合计
    if (stackLabelType === 'value') {
      return {
        label: {
          show: false,
        },
      };
    }

    return {
      label: {
        show: true,
        position: 'top',
        formatter: (params) => {
          const sumValue = calcStackSum(params.data, yFieldKeys);
          return getFormattedValue(formatLabel || sumValue, sumValue) as string;
        },
      },
    };
  }

  // 仅求和隐藏数值标签
  if (stackLabelType === 'sum') {
    return {
      label: {
        show: false,
      },
    };
  }

  return {
    label: {
      show: true,
      position: 'inside',
      formatter: getFormatLabel(formatLabel),
    },
  };
};

export const getLegendConfig = (
  showLegend = false,
  options?: LegendComponentOption,
): LegendComponentOption => {
  if (!showLegend) {
    return { show: false };
  }

  return {
    show: true,
    type: 'scroll',
    height: 12,
    top: 0,
    padding: [0, 0, 50, 0],
    itemGap: 20,
    itemHeight: 10,
    itemWidth: 10,
    // @ts-ignore eslint type error, actually support number[]
    pageIconSize: [6.5, 9],
    pageIconColor: '#284CC0',
    ...options,
  };
};

// PC只允许通过滚动条滚动
export const getDataZoomConfig = (showScrollbar = false) => {
  return {
    dataZoom: showScrollbar ? [BASE_DATA_ZOOM_CONFIG] : [],
  };
};

// 移动允许通过滚动条&坐标轴滚动
export const getMobileDataZoomConfig = (showScrollbar = false) => {
  return {
    dataZoom: showScrollbar
      ? [
          BASE_DATA_ZOOM_CONFIG,
          {
            type: 'inside',
            filterMode: 'filter',
            zoomLock: true,
            start: 0,
            end: 50,
          },
        ]
      : [],
  };
};

export const calcGridBottom = (xFieldTitle?: string | (() => string), showScrollbar?: boolean) => {
  const baseBottom = 4;
  const titleHeight = xFieldTitle ? 24 : 0;
  const scrollBarHeight = showScrollbar ? SCROLL_BAR_HEIGHT + 4 : 0;

  return baseBottom + titleHeight + scrollBarHeight;
};

export const getYFieldConfig = (yFieldConfig?: YFieldConfig) => {
  const {
    yFieldTitle: originalYFieldTitle,
    yNameGap,
    formatYFieldLabel,
    scale,
    rangeSetting,
    range,
  } = yFieldConfig || {};
  const yFieldTitle = getFormattedValue(originalYFieldTitle) as string;

  return {
    type: 'value',
    scale: !scale,
    name: yFieldTitle,
    nameGap: yNameGap,
    axisLine: {
      show: false,
    },
    alignTicks: true,
    axisTick: {
      show: false,
    },
    axisLabel: {
      formatter: (value: string) => {
        return getFormattedValue(formatYFieldLabel || value, value);
      },
    },
    ...getFieldRange(rangeSetting, range),
  };
};

export const getXFieldConfig = (xFieldConfig?: XFieldConfig) => {
  const { xFieldTitle: originalXFieldTitle, sampleLabel, labelRotate } = xFieldConfig || {};

  const xFieldTitle = getFormattedValue(originalXFieldTitle) as string;

  return {
    type: 'category',
    name: xFieldTitle,
    nameGap: 32,
    axisLabel: {
      interval: sampleLabel === false ? 0 : 'auto',
      rotate: labelRotate || 0,
    },
    splitLine: {
      show: false,
    },
    axisTick: {
      alignWithLabel: true,
    },
  };
};
